import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { buttonVariants } from 'components/ui/button';
import ScanProgressPreview from 'components/ScanProgressPreview';
import { SessionError, WSScanStatus } from '../../types';

import { getElapsedSeconds } from 'lib/utils';
import { PATH_ROUTES } from 'routes/path';
import useSocket from 'hooks/useSocket';
import ScanError from './ScanError';
import ScanStopped from './ScanStopped';
import ScanSessionExpired from './ScanSessionExpired';

export default function ScanInProgress({
  error,
  onInitiate,
}: {
  error?: SessionError;
  onInitiate: () => void;
}) {
  const { t } = useTranslation();

  if (!error?.socket_flag) {
    return (
      <ScanWaiting
        startTime={error?.timestamp || ''}
        sessionId={error?.session_id || ''}
        onInitiate={onInitiate}
      />
    );
  }

  return (
    <div className='sm:space-y-12 min-h-[calc(100vh-7rem)] grid place-content-center max-w-5xl mx-auto py-8'>
      <div className='flex-col items-center justify-center hidden px-5 space-y-4 leading-tight text-center lg:flex'>
        <p className='font-medium'>⏳ {t('scan_in_progress')}</p>
        <div className='max-w-xl mx-auto'>
          <p>{t('scan_progress_message')}</p>
        </div>
      </div>

      <div className='flex flex-col items-center justify-center gap-2'>
        <Link
          to={PATH_ROUTES.FACE_SCAN}
          className={buttonVariants({ className: 'px-6 h-9' })}
        >
          {t('close')}
        </Link>
      </div>
    </div>
  );
}

function ScanWaiting({
  startTime,
  sessionId,
  onInitiate,
}: {
  startTime: string;
  sessionId: string;
  onInitiate: () => void;
}) {
  const navigate = useNavigate();

  const [progress, setProgress] = useState<number>(() =>
    startTime ? getElapsedSeconds(startTime) * 1.43 : 0
  );

  const { connectWebSocket, socket, wsStatusData, setWsStatusData } =
    useSocket();

  useEffect(() => {
    if (sessionId)
      connectWebSocket({
        sessionId,
      });
  }, [sessionId]);

  useEffect(() => {
    if (wsStatusData?.status === WSScanStatus.END_SCAN) {
      onInitiate();
    }
  }, [wsStatusData?.status]);

  const onClose = () => {
    setWsStatusData(null);
    if (socket) socket.close();
    navigate(PATH_ROUTES.FACE_SCAN);
  };

  if (wsStatusData?.status === WSScanStatus.SCAN_ERROR) {
    return <ScanError errors={wsStatusData?.error || []} onClose={onClose} />;
  }

  if (wsStatusData?.status === WSScanStatus.STOP_SCAN) {
    return <ScanStopped onClose={onClose} />;
  }

  if (wsStatusData?.status === WSScanStatus.SESSION_EXPIRED) {
    return <ScanSessionExpired onClose={onClose} />;
  }

  return (
    <ScanProgressPreview
      progress={progress}
      onClose={onClose}
      setProgress={setProgress}
      onCompletion={onInitiate}
    />
  );
}
